import React, { useEffect, useState } from 'react';
import { readBookmarks, isBookmarked, toggleBookmark } from '../lib/bookmarks';

/**
 * Feed-style action row. No posting: bookmark (persisted to localStorage),
 * an Open/View link, and a Share button (native share with copy fallback).
 */
export default function PostActionRow({ shareUrl, shareTitle, openUrl, openLabel = 'Open', openExternal = true, onOpenDetails, bookmarkType, bookmarkId, bookmarkTitle, bookmarkDate, bookmarkTime, bookmarkCharge, analytics }) {
    const [saved, setSaved] = useState(() => (bookmarkId && bookmarkType ? isBookmarked(bookmarkType, bookmarkId) : false));

    const handleBookmarkToggle = () => {
        if (!bookmarkId || !bookmarkType) return;
        setSaved(toggleBookmark({ type: bookmarkType, id: bookmarkId, title: bookmarkTitle, date: bookmarkDate, time: bookmarkTime, charge: bookmarkCharge }));
    };

    const handleOpen = () => {
        if (analytics) {
            analytics.trackInteraction('click_feed_open', { url: openUrl });
        }
    };

    const handleShare = async () => {
        const url = shareUrl || (typeof window !== 'undefined' ? window.location.href : '');
        const shareText = shareTitle ? `Check out ${shareTitle} on Pink Wallet` : 'Check this out on Pink Wallet';
        if (navigator.share) {
            try {
                await navigator.share({ title: shareTitle || 'Pink Wallet', text: `${shareText} 🩷🏳️‍⚧️🏳️‍🌈`, url });
            } catch (error) {
                if (error.name !== 'AbortError') console.error('Share failed:', error);
            }
        } else {
            try {
                await navigator.clipboard.writeText(url);
            } catch (error) {
                console.error('Failed to copy link:', error);
            }
        }
    };

    useEffect(() => {
        const sync = () => setSaved((bookmarkId && bookmarkType) ? isBookmarked(bookmarkType, bookmarkId) : false);
        window.addEventListener('pw:bookmarks-changed', sync);
        return () => window.removeEventListener('pw:bookmarks-changed', sync);
    }, [bookmarkId, bookmarkType]);

    return (
        <div className="post-actions">
            {bookmarkType && bookmarkId ? (
                <button
                    type="button"
                    onClick={handleBookmarkToggle}
                    aria-pressed={saved}
                    className={`post-action post-action--bookmark${saved ? ' saved' : ''}`}
                >
                    <svg className="w-[18px] h-[18px]" fill={saved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
                    </svg>
                    <span>{saved ? 'Saved' : 'Save'}</span>
                </button>
            ) : (
                <button type="button" className="post-action post-action--bookmark" aria-pressed="false" aria-label="Bookmark" disabled>
                    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
                    </svg>
                    <span>Save</span>
                </button>
            )}

            {openUrl && openExternal && (
                <a
                    href={openUrl}
                    onClick={handleOpen}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="post-action post-action--primary"
                >
                    <span>{openLabel}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                    </svg>
                </a>
            )}

            {openUrl && !openExternal && (onOpenDetails ? (
                <button
                    type="button"
                    onClick={onOpenDetails}
                    className="post-action post-action--primary"
                    aria-label={openLabel}
                >
                    <span>{openLabel}</span>
                </button>
            ) : (
                <a href={openUrl} className="post-action post-action--primary">
                    <span>{openLabel}</span>
                </a>
            ))}

            <button type="button" onClick={handleShare} className="post-action post-action--share">
                <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                <span>Share</span>
            </button>
        </div>
    );
}